import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import { AmountWithConversions } from '@/models/transaction';
import { createBEMClasses } from '@/utils/classname';
import { formatCurrency } from '@/utils/number';

import { ConversionTooltip } from '../PriceWithDetails/PriceWithDetails';

import '../PriceWithDetails/PriceWithDetails.css';

const { element } = createBEMClasses('price-with-details');

export const BalanceWithDetailsTooltipWithConversions = ({
  balance,
  amountDue,
  amountPaid,
  size = 'medium',
  variant = 'primary',
  direction = 'row',
  tooltipDirection = 'row',
  gap = '0.5rem',
}: {
  balance: AmountWithConversions;
  amountDue: AmountWithConversions;
  amountPaid: AmountWithConversions;
  size?: 'x-small' | 'small' | 'medium';
  variant?: 'primary' | 'secondary' | 'warning';
  direction?: 'row' | 'row-reverse';
  tooltipDirection?: 'row' | 'row-reverse';
  gap?: string;
}) => {
  return (
    <Stack
      style={{
        flexDirection: direction,
        display: 'inline-flex',
        flexWrap: 'nowrap',
      }}
      gap={gap}
    >
      <BalanceTooltip
        balance={balance}
        amountDue={amountDue}
        amountPaid={amountPaid}
        size={size}
        variant={variant}
      />
      {balance.conversions.map((balanceConversion) => {
        const currency = balanceConversion.rate.to;
        const dueConversion = amountDue.conversions.find((c) => c.rate.to === currency);
        const paidConversion = amountPaid.conversions.find((c) => c.rate.to === currency);

        if (!dueConversion || !paidConversion) {
          return null;
        }

        return (
          <Stack
            key={currency}
            style={{
              color: 'var(--color-text-secondary-black)',
              flexDirection: direction,
            }}
            gap={gap}
            alignItems="center"
          >
            <InlineText color="disabled">|</InlineText>
            <Stack
              style={{ flexDirection: tooltipDirection }}
              alignItems="center"
              gap="0.25rem"
              flexWrap="nowrap"
            >
              <ConversionTooltip conversionRate={balanceConversion.rate} />
              <BalanceTooltip
                balance={{
                  amount: balanceConversion.amount,
                  currency,
                  conversions: [],
                }}
                amountDue={{
                  amount: dueConversion.amount,
                  currency,
                  conversions: [],
                }}
                amountPaid={{
                  amount: paidConversion.amount,
                  currency,
                  conversions: [],
                }}
                size={size}
                variant="secondary"
              />
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

const BalanceTooltip = ({
  balance,
  amountDue,
  amountPaid,
  size,
  variant,
}: {
  balance: AmountWithConversions;
  amountDue: AmountWithConversions;
  amountPaid: AmountWithConversions;
  size: 'x-small' | 'small' | 'medium';
  variant: 'primary' | 'secondary' | 'warning';
}) => {
  const inlineTextSize = size === 'x-small' ? '200' : '100';

  return (
    <Tooltip
      variant="light"
      size="medium"
      hasOverlayArrow={false}
      textAlign="start"
      content={
        <table className={element('table')}>
          <tbody>
            <tr className={element('row')}>
              <td>
                <InlineText size="300" color="secondary">
                  <Trans id="balance-with-details.rows.amount-due">Amount due</Trans>
                </InlineText>
              </td>
              <td>
                <InlineText size="300" color="secondary">
                  {formatCurrency(amountDue.amount, amountDue.currency)}
                </InlineText>
              </td>
            </tr>
            <tr className={element('row')}>
              <td>
                <InlineText size="300" color="secondary">
                  {amountPaid.amount < 0 ? (
                    <Trans id="balance-with-details.rows.amount-refunded">Amount refunded</Trans>
                  ) : (
                    <Trans id="balance-with-details.rows.amount-paid">Amount paid</Trans>
                  )}
                </InlineText>
              </td>
              <td>
                <InlineText size="300" color="secondary">
                  {formatCurrency(Math.abs(amountPaid.amount), amountPaid.currency)}
                </InlineText>
              </td>
            </tr>
            <tr className={element('separator')}>
              <td colSpan={2} />
            </tr>
            <tr className={element('row', { 'first-row-of-section': true })}>
              <td>
                <InlineText size="200" weight="medium">
                  <Trans id="balance-with-details.rows.total">Total</Trans>
                </InlineText>
              </td>
              <td>
                <InlineText size="200" weight="medium">
                  {formatCurrency(Math.abs(balance.amount), balance.currency)}
                </InlineText>
              </td>
            </tr>
          </tbody>
        </table>
      }
    >
      <Button variant="style-less">
        <InlineText
          size={inlineTextSize}
          weight={size === 'x-small' ? 'regular' : 'medium'}
          color={variant}
          className={element('trigger', { size })}
        >
          {formatCurrency(Math.abs(balance.amount), balance.currency)}
        </InlineText>
      </Button>
    </Tooltip>
  );
};
